import { Pet, PetSpecies, PetStatusEffect } from 'src/replicant/buruburu/pet';
import { Visit } from 'src/replicant/visit';
import StateObserver from 'src/StateObserver';
import { fromEntityId, PetEntity } from '../logic/EntityManager';
import {
  AnimationState,
  getPetAnimation,
  getStatusAnimationName,
} from './appearance.config';
import { Attributes, petActions } from '../pet/pet.redux';
import { isSceneEntered } from 'src/lib/stateUtils';
import { State } from 'src/state';
import { createEmitter } from 'src/lib/Emitter';
import { getDynamicTestBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';

export class PetAppearanceController {
  constructor(protected entity: PetEntity) {}

  public init() {
    createEmitter(this.entity, this.getState.bind(this)).addListener(
      this.updateState.bind(this),
    );
  }

  public setSpecies(species: PetSpecies) {
    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(
      petActions.setSpecies({
        id,
        payload: species,
      }),
    );
  }

  public setStatusEffects(
    statusEffects: Partial<Record<PetStatusEffect, boolean>>,
  ) {
    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(
      petActions.setStatusEffects({
        id,
        payload: statusEffects,
      }),
    );
  }

  public setAttributes(attributes: Partial<Attributes>) {
    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(
      petActions.setAttributes({ id, payload: attributes }),
    );
  }

  public setAction(action: AnimationState | null) {
    const { id } = fromEntityId(this.entity.entityId);
    StateObserver.dispatch(petActions.setAction({ id, payload: action }));
  }

  public setFlipped(flipX: boolean) {
    this.entity.setFlipped(flipX);
  }

  private getState(state?: State) {
    const { pet, care, conversation, user, ui } =
      state ?? StateObserver.getState();

    const { id } = fromEntityId(this.entity.entityId);

    if (!pet[id] || !conversation[id]) return null;

    const { action, statusEffects, species } = pet[id];
    const { activeScript } = conversation[id];

    const isVisiting = Visit.getters(user).isVisiting();

    const result = {
      id,
      action,
      species,
      ...statusEffects,
      ...pet[id].attributes,
      activeScript,
      currentCareAction: !!care.currentCareAction,
      isVisiting,
    };

    // Hide status animation while care action is active
    const hideStatusVfx =
      !(isSceneEntered('livingRoom') || isSceneEntered('friendsLivingRoom')) || // Are we in the living room or friend's living room?
      result.currentCareAction || // Is a care action in progress?
      result.action === 'hang' || // Is the pet transitioning scenes?
      (isSceneEntered('friendsLivingRoom') && user.duo.debut) || // Is pet debuting?
      ui.visibleStats !== result.id; // Is pet stats visible?

    return { ...result, hideStatusVfx };
  }

  public updateState(state?: ReturnType<PetAppearanceController['getState']>) {
    state = state ?? this.getState();

    if (!state?.species) return;

    this.entity.setSpecies(state.species);
    this.entity.setAnimation(
      getPetAnimation(state, this.entity.moving ? 'walking' : 'idle'),
    );

    const petState = Pet.ruleset.getPetState(state, state, state.isVisiting);
    const statusAnimation =
      !state || state.hideStatusVfx ? null : getStatusAnimationName(petState);
    this.entity.setStatusAnimation(statusAnimation);
  }
}
